<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <div class="page-title">
        <h2>{{ $t("supplier.feedSuppliers") }}</h2>
        <v-spacer></v-spacer>

        <v-btn color="primary" dark @click="addNewSupplier()">
          {{ $t("supplier.createNewSupplier") }}
        </v-btn>
      </div>
      <v-card
        v-if="!this.showNotFound || this.search"
        elevation="0"
        class="px-5"
      >
        <v-card-text>
          <v-data-table
            :headers="this.tableHeaders"
            :items="suppliers"
            :header-props="{
              'sort-icon': 'mdi-unfold-more-horizontal',
            }"
            :footer-props="{
              'items-per-page-options': itemsPerPageOptions,
              'show-first-last-page': true,
              'show-current-page': true,
            }"
            :options.sync="tableOptions"
            :items-per-page="itemsPerPage"
            :loading="$apollo.loading"
            :loading-text="$t('loadingPleaseWait')"
            hide-default-footer
          >
            <template v-slot:top>
              <div class="d-flex mt-5">
                <v-text-field
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  height="5px"
                  :placeholder="$t('search')"
                  dense
                  v-model="search"
                  class="shrink"
                ></v-text-field>
                <v-spacer></v-spacer>
                <div class="py-3">
                  {{ from }}
                  -
                  {{ to }}
                  of {{ totalCount }}
                </div>
                <v-btn
                  class="mx-2"
                  fab
                  small
                  outlined
                  color="primary"
                  :disabled="tableOptions.page <= 1"
                  @click="pageChange(false)"
                >
                  <v-icon dark> mdi-chevron-left </v-icon>
                </v-btn>
                <v-btn
                  class="mx-2"
                  fab
                  small
                  outlined
                  color="primary"
                  :disabled="tableOptions.page >= totalPages"
                  @click="pageChange(true)"
                >
                  <v-icon dark> mdi-chevron-right </v-icon>
                </v-btn>
                <v-menu
                  v-model="menu"
                  :close-on-content-click="false"
                  :nudge-width="200"
                  left
                  :min-width="417"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      class="mx-2"
                      fab
                      small
                      outlined
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon dark> mdi-cog-outline </v-icon>
                    </v-btn>
                  </template>

                  <v-card width="417">
                    <v-list>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>{{
                            $t("customization")
                          }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider></v-divider>
                      <div class="pl-3 pt-2 font-weight-normal">
                        {{ $t("recordsPerPage") }}
                      </div>
                      <v-list-item>
                        <v-list-item-content>
                          <v-slider
                            v-model="itemsPerPageSlider"
                            :tick-labels="itemsPerPageOptions"
                            :max="3"
                            step="1"
                            ticks="always"
                            tick-size="4"
                          ></v-slider>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider class="mt-n3"></v-divider>
                    </v-list>
                    <v-card-text>
                      <v-container fluid>
                        <v-row>
                          <v-col
                            class="mt-n9"
                            cols="6"
                            v-for="header in headers"
                            :key="header.value"
                          >
                            <v-checkbox
                              v-if="header.text.length > 0"
                              v-model="selectedHeaders"
                              :label="header.text"
                              color="primary"
                              :value="header.text"
                              hide-details
                            ></v-checkbox>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>
                    <v-layout justify-center>
                      <v-card-actions class="mb-5">
                        <v-spacer></v-spacer>

                        <v-btn
                          outlined
                          color="primary"
                          width="190"
                          @click="resetDefault"
                        >
                          {{ $t("restoreDefault") }}
                        </v-btn>
                        <v-btn color="primary" @click="resetTableStructure">
                          {{ $t("apply") }}
                        </v-btn>
                      </v-card-actions>
                    </v-layout>
                  </v-card>
                </v-menu>
              </div>
            </template>
            <template v-slot:progress>
              <v-progress-linear
                color="primary"
                :height="8"
                indeterminate
              ></v-progress-linear>
            </template>
            <template v-if="suppliers.length > 0" v-slot:body="{ items }">
              <tbody>
                <tr
                  v-for="item in items"
                  :key="item.id"
                  style="position: relative; width: 0; height: 0"
                >
                  <template v-for="h in tableHeaders">
                    <td :key="h.text" v-if="h.value == 'supplierName'">
                      <div
                        class="d-flex align-center"
                        style="cursor: pointer"
                        @click="viewDetailSupplier(item.id)"
                      >
                        <img
                          :src="
                            item['profilePic']
                              ? item['profilePic']
                              : '/img/No_Image_Available.jpg'
                          "
                          style="height: 50px; width: 50px; border-radius: 50%"
                        />
                        <span
                          class="text-decoration-underline ml-3"
                          style="color: #4a70f6"
                        >
                          {{ item[h.value] }}
                        </span>
                      </div>
                    </td>

                    <td
                      :key="h.text"
                      v-else-if="h.value == 'supplierIngredient'"
                    >
                      {{ item[h.value].length }}
                    </td>

                    <td :key="h.text" v-else-if="h.value == 'action'">
                      <div>
                        <v-menu left :offset-x="true" rounded>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon v-bind="attrs" v-on="on" class="mt-1"
                              >mdi-dots-vertical
                            </v-icon>
                          </template>
                          <v-list class="user-menu" style="width: 200px">
                            <v-list-item
                              style="padding: 0"
                              @click="updateSupplier(item.id)"
                            >
                              <v-col cols="auto">
                                <v-btn
                                  small
                                  rounded
                                  color="green"
                                  depressed
                                  icon
                                >
                                  <v-icon size="large"> mdi-pencil </v-icon>
                                </v-btn>
                              </v-col>
                              <v-list-item-title style="font-size: 14px"
                                >{{ $t('edit') }}</v-list-item-title
                              >
                            </v-list-item>
                            <v-list-item
                              style="padding: 0"
                              @click="deleteSupplier(item.id)"
                            >
                              <v-col cols="auto">
                                <v-btn
                                  x-small
                                  rounded
                                  color="red2"
                                  depressed
                                  fab
                                >
                                  <v-icon color="red">
                                    mdi-trash-can-outline
                                  </v-icon>
                                </v-btn>
                              </v-col>

                              <v-list-item-title style="font-size: 14px"
                                >{{ $t('geneticsAndBreeding.delete') }}</v-list-item-title
                              >
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>
                    </td>

                    <td :key="h.value" v-else>
                      {{ item[h.value] }}
                    </td>
                  </template>
                </tr>
              </tbody>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
      <div v-if="this.showNotFound && !this.search">
        <v-card class="my-4" elevation="0">
          <v-card-text>
            <div
              style="height: 80vh"
              class="d-flex flex-column justify-center align-center"
            >
              <img src="/img/Notes.svg" />
              <h4 class="red--text">No Supplier Found</h4>
              <p class="text-center pt-4" style="width: 300px">
                Please create a Feed Supplier using the button ‘’Create New
                Supplier” above.
              </p>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </v-container>
    <add-new-supplier-dialog
      v-if="showAddNewSupplierDialog"
      :dialogI="showAddNewSupplierDialog"
      @closeDialog="showAddNewSupplierDialog = false"
      @supplierCreated="addSupplier"
    ></add-new-supplier-dialog>
    <update-supplier-dialog
      v-if="showUpdateSupplierDialog"
      :dialogI="showUpdateSupplierDialog"
      :supplierID="this.detailSupplier"
      @closeDialog="showUpdateSupplierDialog = false"
      @supplierUpdated="updatedSupplier"
    ></update-supplier-dialog>
    <supplier-detail-dialog
      v-if="showSupplierDetailDialog"
      :dialogI="showSupplierDetailDialog"
      :supplierID="this.detailSupplier"
      @closeDialog="showSupplierDetailDialog = false"
      @editSupplier="editSupplierFromDetail"
      @removeSupplier="deleteSupplier"
    ></supplier-detail-dialog>
  </div>
</template>

<script>
import loadingMixin from "@/mixins/LoadingMixin";
import Breadcrumb from "@/components/core/Breadcrumb";
import AddNewSupplierDialog from "./AddNewSupplierDialog.vue";
import UpdateSupplierDialog from "./UpdateSupplierDialog.vue";
import SupplierDetailDialog from "./SupplierDetailDialog.vue";
import gql from "graphql-tag";
import { suppliers } from "@/graphql/Supplier.gql";
export default {
  apollo: {
    suppliers: {
      query: suppliers,
      fetchPolicy: "network-only",
      variables() {
        return {
          queryData: {
            page: this.tableOptions.page,
            limit: this.tableOptions.itemsPerPage,
            searchPhrase: this.search,
          },
        };
      },
      update: data => data.suppliers.rows,
      result({ data }) {
        if (data.suppliers.rows.length <= 0) {
          this.showNotFound = true;
        }
        this.totalSuppliers = data.suppliers.totalCount;
        this.totalPages = Math.ceil(
          this.totalSuppliers / this.tableOptions.itemsPerPage
        );
        this.from =
          (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage + 1;
        this.to =
          this.tableOptions.page * this.tableOptions.itemsPerPage -
          (this.tableOptions.itemsPerPage - data.suppliers.count);
        this.totalCount = data.suppliers.totalCount;
      },
    },
  },

  components: {
    breadcrumb: Breadcrumb,
    "add-new-supplier-dialog": AddNewSupplierDialog,
    "update-supplier-dialog": UpdateSupplierDialog,
    "supplier-detail-dialog": SupplierDetailDialog,
  },

  async beforeMount() {
    this.startLoading();
  },

  async mounted() {
    document.title = "Feed Suppliers";
    this.tableHeaders = this.headers;
    this.selectedHeaders = this.allSelectedHeaders;
    this.stopLoading();
  },

  watch: {
    tableOptions: {
      handler() {
        this.$apollo.queries.suppliers.refetch();
      },
      deep: true,
    },
  },
  methods: {
    searchSuppliers() {
      this.startLoading();
      console.log(this.search);
      if (this.search == "") return;
      else {
        this.tableOptions.page = 1;
        this.$apollo.queries.suppliers.refetch();
      }
      this.stopLoading();
    },
    addNewSupplier() {
      this.showAddNewSupplierDialog = true;
    },
    addSupplier(newSupplier) {
      if (newSupplier) {
        this.suppliers.unshift(newSupplier);
        this.showAddNewSupplierDialog = false;
        this.$apollo.queries.suppliers.refetch();
        this.showNotFound = false;
      }
    },

    updatedSupplier() {
      this.$apollo.queries.suppliers.refetch();
      this.showUpdateSupplierDialog = false;
    },

    async viewDetailSupplier(id) {
      this.startLoading();

      this.showSupplierDetailDialog = true;
      this.detailSupplier = Number(id);
      this.stopLoading();
    },

    async editSupplierFromDetail(supplierID) {
      this.showSupplierDetailDialog = false;
      this.updateSupplier(supplierID);
    },

    async deleteSupplier(itemID) {
      this.startLoading();
      this.$apollo
        .mutate({
          mutation: gql`
            mutation ($id: Int!) {
              removeSupplier(id: $id) {
                success
                message
              }
            }
          `,
          variables: {
            id: Number(itemID),
          },
        })
        .then(async () => {
          this.$notify({
            title: `Supplier deleted successfully`,
            text: "",
            type: "success",
          });
          this.$apollo.queries.suppliers.refetch();
          this.stopLoading();
        })
        .catch(async error => {
          console.log(error);
          this.stopLoading();
        });
    },

    async updateSupplier(itemID) {
      this.startLoading();
      this.showUpdateSupplierDialog = true;
      this.detailSupplier = itemID;
      this.stopLoading();
    },

    closeSupplierDetails() {
      this.showUpdateSupplierDialog = false;
    },

    resetTableStructure() {
      this.startLoading();
      this.tableHeaders = this.headers.filter(header =>
        this.selectedHeaders.includes(header.text.toString())
      );
      this.tableHeaders.push({
        text: "",
        value: "action",
        sortable: false,
        class: "black--text",
      });
      this.tableOptions.itemsPerPage =
        this.itemsPerPageOptions[this.itemsPerPageSlider];
      this.menu = false;
      this.stopLoading();
    },

    resetDefault() {
      this.tableHeaders = this.headers;
      this.selectedHeaders = this.allSelectedHeaders;
      this.menu = false;
    },

    pageChange(t) {
      if (t) this.tableOptions.page = this.tableOptions.page + 1;
      else this.tableOptions.page = this.tableOptions.page - 1;

      this.$apollo.queries.suppliers.refetch();
    },
  },
  data() {
    return {
      showNotFound: false,
      showAddNewSupplierDialog: false,
      showUpdateSupplierDialog: false,
      showSupplierDetailDialog: false,
      detailSupplier: null,
      menu: false,
      itemsPerPage: 10,
      itemsPerPageSlider: 0,
      itemsPerPageOptions: [5, 10, 25, 50],
      supplier: null,

      to: 0,
      from: 0,
      totalCount: 0,
      totalPages: 1,
      search: "",
      selected: [],
      totalSuppliers: 1,
      selectedItem: false,
      loading: false,
      length: 0,
      pageCount: 1,
      tableOptions: { page: 1, itemsPerPage: 10 },
      loadingText: "Loading Suppliers... Please wait",
      breadcrumbs: [
        {
          text: this.$t("dashboard"),
          disabled: false,
          to: "/",
        },
        {
          text: this.$t("Feed Management"),
          disabled: true,
          to: { name: "Feed Management" },
        },
        {
          text: this.$t("feedMgt.feedSuppliers"),
          disabled: true,
          to: { name: "Feed Suppliers" },
        },
      ],
      tableHeaders: ["action"],
      allSelectedHeaders: [
        this.$t("supplier.supplierName"),
        this.$t("supplier.phoneNumber"),
        this.$t("supplier.emailAddress"),
        "Address",
        this.$t("supplier.licenseNumber"),
        "No.of Items",
        "action",
      ],
      headers: [
        {
          text: this.$t("supplier.supplierName"),
          align: "start",
          value: "supplierName",
          class: "black--text",
        },
        {
          text: this.$t("supplier.phoneNumber"),
          value: "phoneNo",
          class: "black--text",
        },
        {
          text: this.$t("supplier.emailAddress"),
          value: "email",
          class: "black--text",
        },
        {
          text: this.$t("feedMgt.address"),
          value: "countryId",
          class: "black--text",
        },
        {
          text: this.$t("supplier.licenseNumber"),
          value: "licenseNumber",
          class: "black--text",
        },
        {
          text: this.$t("feedMgt.noOfItems"),
          value: "supplierIngredient",
          class: "black--text",
        },
        {
          text: "",
          value: "action",
          sortable: false,
          class: "black--text",
        },
      ],
      selectedHeaders: [],
      suppliers: [],
    };
  },
  mixins: [loadingMixin],
};
</script>

<style lang="scss" scoped>
::v-deep .v-data-table-header {
  background: $secondary2-color;
}

::v-deep .v-data-table-header span {
  // text-transform: uppercase !important;
  color: black;
}

::v-deep .v-data-table-header th {
  white-space: nowrap;
}

.table-header {
  background: $secondary2-color;
}

.table-header-span {
  text-transform: uppercase !important;
  color: black !important;
}
</style>
